import { NoteObservableEditor } from "@/components/note/NoteObservableEditor";
import { TextDiff } from "@/components/note/diff/TextDiff";
import { NoteEditorSourceBar } from "@/components/note/editor/source-bar/NoteEditorSourceBar";
import {
  NOTE_EDITOR_TOP_BAR_HEIGHT,
  NoteEditorTopBar,
} from "@/components/note/editor/top-bar/NoteEditorTopBar";
import { NoteEditorMode } from "@/components/note/types";
import { NoteSkeleton } from "@/components/skeleton/NoteSkeleton";
import { MdsButton, MdsButtonVariant } from "@/design-system/components/button";
import { mdsMediaQueries } from "@/design-system/foundations";
import { css } from "@/domains/emotion";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { logger } from "@/modules/logger";
import { forceReloadWindow } from "@/modules/window/reload";
import { ChatHistory } from "@/store/chat/ChatHistory";
import { IContactModel } from "@/store/contacts/types";
import { NoteObservable } from "@/store/note/NoteObservable";
import { INoteObservable, NoteContentVersion } from "@/store/note/types";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { UpdateNoteContentUsingDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingDiffOperation";
import { useAppStore } from "@/store/utils/hooks";
import styled from "@emotion/styled";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

interface NoteEditorProps {
  isSidePanelOpen: boolean;
  chatHistory?: ChatHistory;
  goToMention?: INotesViewPageStore["goToMention"];
  openFileUploadRejectedModal?: INotesViewPageStore["openFileUploadRejectedModal"];
  openImageUploadRejectedModal?: INotesViewPageStore["openImageUploadRejectedModal"];
  noteObservable?: INoteObservable;
  myAccount?: IContactModel;
  inSidePanel?: boolean;
  highlightText?: string;
  autoFocus?: boolean;
  versions?: NoteContentVersion[];
  isMainEditor?: boolean;
  onNoteRendered?: (noteId: string) => void;
}

export const NoteEditor = observer<NoteEditorProps>(function NoteEditor({
  chatHistory,
  goToMention,
  openFileUploadRejectedModal,
  openImageUploadRejectedModal,
  noteObservable,
  myAccount,
  inSidePanel,
  highlightText,
  autoFocus,
  versions,
  isMainEditor,
  onNoteRendered,
}) {
  const showSkeleton = noteObservable && !noteObservable.isNoteContentDocumentLoaded;
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const { store } = useAppStore();

  const shouldShowProcessingIndicator =
    noteObservable instanceof NoteObservable && noteObservable.isProcessingSuggestion;

  const { titleText, title } = computed(() => {
    if (!noteObservable) return { titleText: "", title: "" };

    const suggestionData = noteObservable.suggestionData;
    if (suggestionData?.suggested_primary_label) {
      const oldText = noteObservable.title === UNTITLED_NOTE_TITLE ? "" : noteObservable.title;
      const titleText = suggestionData.suggested_primary_label;
      const title = <TextDiff oldText={oldText} newText={titleText} />;
      return { titleText, title };
    }

    for (const { operation } of noteObservable.memoryQueue?.operations.toReversed() ?? []) {
      if (operation.operationKind !== "UPDATE_NOTE_CONTENT_USING_DIFF") continue;

      const primaryLabel = (operation as UpdateNoteContentUsingDiffOperation).primaryLabel;
      if (primaryLabel) return { titleText: primaryLabel, title: primaryLabel };
    }
    const titleText = noteObservable.title || UNTITLED_NOTE_TITLE;
    return { titleText, title: titleText };
  }).get();

  useTitle(titleText);
  useLockNote(noteObservable);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (showSkeleton) {
      timeoutId = setTimeout(() => {
        logger.warn({
          message: "[NoteEditor][showRefreshButton] Time elapsed, showing refresh button",
          info: {
            noteId: noteObservable?.id,
            noteTitle: noteObservable?.title,
          },
        });

        setShowRefreshButton(true);
      }, 5000);
    } else {
      setShowRefreshButton(false);
    }
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSkeleton]);

  /**
   * Track views of the note editor.
   */
  useEffect(() => {
    if (!noteObservable) return;
    store.notes.setNewNoteIsBeingCreated(false);

    trackEvent(TrackedEvent.NoteView, {
      note_id: noteObservable.id,
      note_primary_label: noteObservable.title,
      is_in_side_panel: inSidePanel,
    });
  }, [inSidePanel, noteObservable, store]);

  if (!noteObservable || !noteObservable.spaceAccountNote) return null;

  return (
    <div className={fullHeight}>
      <ContentWrapper>
        <NoteEditorTopBar title={title} note={noteObservable} inSidePanel={inSidePanel} />
        {showSkeleton && (
          <>
            <NoteSkeleton />
            {showRefreshButton && (
              <RefreshButtonWrapper>
                <MdsButton
                  label={`This note is taking longer than expected to load. Refresh?`}
                  variant={MdsButtonVariant.Filled}
                  onClick={() => forceReloadWindow()}
                />
              </RefreshButtonWrapper>
            )}
          </>
        )}
        {!showSkeleton && noteObservable && (
          <NoteObservableEditor
            autoFocus={autoFocus}
            className={editorStyles}
            chatHistory={chatHistory}
            goToMention={goToMention}
            openFileUploadRejectedModal={openFileUploadRejectedModal}
            openImageUploadRejectedModal={openImageUploadRejectedModal}
            noteObservable={noteObservable}
            myAccount={myAccount}
            mode={NoteEditorMode.Editable}
            highlightText={highlightText}
            versions={versions}
            isMainEditor={isMainEditor}
            onNoteRendered={onNoteRendered}
          />
        )}
        {shouldShowProcessingIndicator && <PulsingOverlay hasSources={noteObservable.hasSources} />}
      </ContentWrapper>
      {noteObservable && <NoteEditorSourceBar note={noteObservable} />}
    </div>
  );
});

const fullHeight = css({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  position: "relative",
  width: "100%",
});

const ContentWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  minHeight: 0,
});

// Note: left padding is always 16px larger than right padding due to the scrollbar.
// Only issue is on mobile where the scrollbar is overlay it's not part of the width calculations.
const editorStyles = css({
  background: `linear-gradient(white 30%, rgba(255, 255, 255, 0)), linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%, radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, .08), rgba(0, 0, 0, 0)), radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, .08), rgba(0, 0, 0, 0)) 0 100%`,
  backgroundRepeat: "no-repeat",
  backgroundColor: "#fff",
  backgroundSize: "100% 30px, 100% 30px, 100% 12px, 100% 12px",
  backgroundAttachment: "local, local, scroll, scroll",
  boxSizing: "border-box",
  cursor: "text",
  display: "flex",
  flexDirection: "column",
  height: `calc(100% - ${NOTE_EDITOR_TOP_BAR_HEIGHT})`,
  overflowY: "auto",
  overflowX: "hidden",
  position: "relative",
  // Consistent sizing whether the scrollbar is visible or not.
  scrollbarGutter: "stable",

  "--bullet-disc-font-size": "22px",
  "--bullet-circle-font-size": "9px",
  "--bullet-square-font-size": "8px",

  "--extra-padding-left": `56px`,
  "--extra-padding-right": `56px`,
  "--extra-padding-bottom": "200px",

  [mdsMediaQueries().rules.tablet]: {
    "--extra-padding-left": `40px`,
    "--extra-padding-right": `40px`,
    "--extra-padding-bottom": "100px",
  },

  [mdsMediaQueries().rules.phone]: {
    "--extra-padding-left": `16px`,
    "--extra-padding-right": `16px`,
    "--extra-padding-bottom": "100px",
  },
});

const PulsingOverlay = styled.div<{ hasSources: boolean }>(({ hasSources }) => ({
  position: "absolute",
  top: NOTE_EDITOR_TOP_BAR_HEIGHT,
  left: 0,
  right: 0,
  bottom: hasSources ? "64px" : 0,
  pointerEvents: "none",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "100%",
    height: "100%",
    transform: "translate(-50%, -50%)",
    animation: "pulse 2s ease-in-out infinite",
    background:
      "radial-gradient(circle at center, rgba(60, 111, 246, 0.1) 0%, rgba(60, 111, 246, 0) 150%)",
    opacity: 0,
  },

  "@keyframes pulse": {
    "0%": {
      opacity: 0,
      transform: "translate(-50%, -50%) scale(1)",
    },
    "50%": {
      opacity: 1,
      transform: "translate(-50%, -50%) scale(1.2)",
    },
    "100%": {
      opacity: 0,
      transform: "translate(-50%, -50%) scale(1)",
    },
  },
}));

/** Preserve the default document title */
const defaultTitle = document.title;

/** Prefix document title with Note title */
const useTitle = (title: string): void => {
  document.title = title ? `${title} - Mem` : defaultTitle;
  useEffect(() => {
    return () => {
      document.title = defaultTitle;
    };
  }, []);
};

const useLockNote = (note: INoteObservable | undefined): void => {
  useEffect(() => {
    note?.lock();
    return () => {
      note?.unlock();
    };
  }, [note]);
};

const RefreshButtonWrapper = styled.div({
  display: "flex",
  justifyContent: "center",
});
